import React, { useState, useMemo } from "react";
import styles from "./monthsHighestRevenueTable.module.css";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import { formatNumberToCurrency } from "../../../utils/formatter";

const MonthsHighestRevenueTable = ({ revenueData = [], showAll = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const validRevenueData = useMemo(() => {
    return revenueData
      .filter((item) =>
        (item.mes || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const cmp = a.valorFaturado - b.valorFaturado;
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [revenueData, searchTerm, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(validRevenueData.length / itemsPerPage),
    [validRevenueData.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    if (showAll) return validRevenueData;
    const start = (currentPage - 1) * itemsPerPage;
    return validRevenueData.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, validRevenueData, showAll]);

  const handlePageClick = (page) => setCurrentPage(page);

  const emptyRows = showAll ? [] : Array(Math.max(0, itemsPerPage - currentTableData.length)).fill(null);
  const hasNoData = validRevenueData.length === 0;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Meses com Maior Faturamento</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div className={styles.popover}>
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Tabela que exibe os meses com maiores valores de faturamento, permitindo identificar períodos de maior receita. Os dados podem ser filtrados por mês/ano e ordenados por valor faturado.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  className={styles.closeBtn}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar mês..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          <select
            className={styles.orderWrapper}
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="desc">Maior Faturamento</option>
            <option value="asc">Menor Faturamento</option>
          </select>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Mês/Ano</th>
            <th>Valor Faturado</th>
          </tr>
        </thead>
        <tbody>
          {hasNoData ? (
            <tr>
              <td colSpan="2" className={styles.noData}>
                Nenhum dado de faturamento encontrado
              </td>
            </tr>
          ) : (
            <>
              {currentTableData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.mes}</td>
                  <td>{formatNumberToCurrency(item.valorFaturado)}</td>
                </tr>
              ))}
              {emptyRows.map((_, idx) => (
                <tr key={`empty-${idx}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {!showAll && totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={page === currentPage ? styles.activePage : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonthsHighestRevenueTable;
